import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import { Card } from "@nimit9/signet-ui/components/card"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { ResponsiveSheet } from "@nimit9/signet-ui/components/responsive-sheet"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useGoals } from "@/hooks/use-goals"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { useUIStore } from "@/store/ui"
import { AddEmiForm } from "./-add-emi-form"
import { AddGoalForm } from "./-add-goal-form"
import { AddSinkingFundForm } from "./-add-sinking-fund-form"
import { ContributeForm } from "./-contribute-form"
import { EmiCard } from "./-emi-card"
import { GoalCard } from "./-goal-card"
import { buildGoalsHeadline, isGoalOnTrack } from "./-headline"
import { SinkingFundRow } from "./-sinking-fund-row"

export const Route = createFileRoute("/goals/")({
  component: Goals,
})

// ─── Main Page ───
function Goals() {
  const fmt = useCurrencyFormat()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useGoals()
  const queryClient = useQueryClient()
  const openPanel = useUIStore((s) => s.openPanel)
  const pendingAction = useUIStore((s) => s.pendingAction)
  const setPendingAction = useUIStore((s) => s.setPendingAction)
  const [showAddGoal, setShowAddGoal] = useState(false)
  const [showAddEmi, setShowAddEmi] = useState(false)
  const [showAddSinkingFund, setShowAddSinkingFund] = useState(false)
  const [contributingTo, setContributingTo] = useState<string | null>(null)

  useEffect(() => {
    if (pendingAction === "new_goal") {
      setShowAddGoal(true)
      setPendingAction(null)
    } else if (pendingAction === "log_contribution") {
      // Open first active goal's panel if available
      const firstGoal = data?.goals?.[0]
      if (firstGoal) openPanel("goal", { goal: firstGoal as unknown as Record<string, unknown> })
      setPendingAction(null)
    }
  }, [pendingAction, setPendingAction, data?.goals, openPanel])

  const deleteEmi = useMutation({
    mutationFn: (id: string) => api.delete(`/goals/emis/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      queryClient.invalidateQueries({ queryKey: ["upcoming"] }) // (#72 Q6)
      // net worth derives emiLiabilities live from active EMIs (net-worth.ts) — refetch it.
      queryClient.invalidateQueries({ queryKey: ["net-worth"] })
    },
  })

  const markEmiPaid = useMutation({
    mutationFn: ({ id, paidMonths }: { id: string; paidMonths: number }) =>
      api.patch(`/goals/emis/${id}`, { paidMonths }),
    onMutate: async ({ id, paidMonths }) => {
      await queryClient.cancelQueries({ queryKey: ["goals"] })
      const previous = queryClient.getQueryData(["goals"])
      queryClient.setQueryData(["goals"], (old: Record<string, unknown> | undefined) => {
        if (!old) return old
        const emis = old.emis as Array<Record<string, unknown>>
        return { ...old, emis: emis.map((e) => (e.id === id ? { ...e, paidMonths } : e)) }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(["goals"], (context as { previous: unknown })?.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      queryClient.invalidateQueries({ queryKey: ["upcoming"] }) // (#72 Q6)
      // paidMonths changes the live emiLiabilities in net worth (net-worth.ts).
      queryClient.invalidateQueries({ queryKey: ["net-worth"] })
    },
  })

  const deleteSinkingFund = useMutation({
    mutationFn: (id: string) => api.delete(`/goals/sinking-funds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      queryClient.invalidateQueries({ queryKey: ["upcoming"] }) // (#72 Q6)
    },
  })

  if (isLoading) {
    return (
      <>
        <Header title="Goals & Planning" showPeriod={false} />
        <PageWrapper>
          <div className="space-y-6">
            {/* Goals section skeleton */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="space-y-3 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle"
                  >
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                    <Skeleton className="h-2 w-full rounded-full" />
                    <div className="flex justify-between">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* EMIs section skeleton */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="space-y-3 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle"
                  >
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-5 w-14 rounded-full" />
                    </div>
                    <div className="flex justify-between">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sinking funds section skeleton */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="space-y-3 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle"
                  >
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-2 w-full rounded-full" />
                    <div className="flex justify-between">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Goals & Planning" showPeriod={false} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  const goals = data?.goals || []
  const emis = data?.emis || []
  const sinkingFunds = data?.sinkingFunds || []
  const hasAnything = goals.length + emis.length + sinkingFunds.length > 0

  const activeEmis = emis.filter((e) => e.status === "active")
  const totalEmi = activeEmis.reduce((s, e) => s + parseFloat(e.emiAmount), 0)
  const headline = buildGoalsHeadline({ goals, emis, sinkingFunds })

  // Forms open in a bottom Drawer on phone and a Dialog on desktop.
  const sheets = (
    <>
      {showAddGoal && (
        <ResponsiveSheet title="New goal" testId="goal-sheet" onClose={() => setShowAddGoal(false)}>
          <AddGoalForm onClose={() => setShowAddGoal(false)} />
        </ResponsiveSheet>
      )}
      {showAddEmi && (
        <ResponsiveSheet title="New EMI" testId="emi-sheet" onClose={() => setShowAddEmi(false)}>
          <AddEmiForm onClose={() => setShowAddEmi(false)} />
        </ResponsiveSheet>
      )}
      {showAddSinkingFund && (
        <ResponsiveSheet
          title="New sinking fund"
          testId="sinking-fund-sheet"
          onClose={() => setShowAddSinkingFund(false)}
        >
          <AddSinkingFundForm onClose={() => setShowAddSinkingFund(false)} />
        </ResponsiveSheet>
      )}
      {contributingTo && (
        <ResponsiveSheet
          title="Log contribution"
          testId="contribute-sheet"
          onClose={() => setContributingTo(null)}
        >
          <ContributeForm goalId={contributingTo} onClose={() => setContributingTo(null)} />
        </ResponsiveSheet>
      )}
    </>
  )

  if (!hasAnything) {
    return (
      <>
        <Header title="Goals & Planning" showPeriod={false} />
        <PageWrapper>
          <EmptyState
            title="No goals set"
            description="Create your first financial goal, add an EMI, or set up a sinking fund"
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => setShowAddGoal(true)}>+ Goal</Button>
                <Button variant="outline" onClick={() => setShowAddEmi(true)}>
                  + EMI
                </Button>
                <Button variant="outline" onClick={() => setShowAddSinkingFund(true)}>
                  + Sinking fund
                </Button>
              </div>
            }
          />
          {sheets}
        </PageWrapper>
      </>
    )
  }

  const sortedGoals = [...goals].sort(
    // Completed goals sink below the ones still in progress (#67).
    (a, b) => Number(a.status === "completed") - Number(b.status === "completed"),
  )
  const emiError = deleteEmi.isError || markEmiPaid.isError

  return (
    <>
      <Header title="Goals & Planning" showPeriod={false} />
      <PageWrapper>
        <div
          data-testid="goals-page"
          data-layout={isDesktop ? "desktop" : "phone"}
          className="flex min-w-0 flex-col gap-6"
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {/* Goals section */}
          <section className="flex min-w-0 flex-col gap-3">
            <SectionHeader
              title="Goals"
              action={
                <Button variant="link" size="sm" onClick={() => setShowAddGoal(true)}>
                  + Add goal
                </Button>
              }
            />
            {goals.length > 0 ? (
              <div data-testid="goals-grid" className={CARD_GRID}>
                {sortedGoals.map((g) => (
                  <GoalCard
                    key={g.id}
                    goal={g}
                    behind={!isGoalOnTrack(g)}
                    onContribute={setContributingTo}
                    onOpen={(goal) =>
                      openPanel("goal", { goal: goal as unknown as Record<string, unknown> })
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-caption text-text-muted">No goals yet</p>
            )}
          </section>

          {/* EMIs section */}
          <section className="flex min-w-0 flex-col gap-3">
            <SectionHeader
              title="EMIs"
              meta={totalEmi > 0 ? <>· {fmt(totalEmi)}/mo total</> : null}
              action={
                <Button variant="link" size="sm" onClick={() => setShowAddEmi(true)}>
                  + Add EMI
                </Button>
              }
            />
            {emiError && (
              <p className="text-caption text-accent-amber">
                {((deleteEmi.error || markEmiPaid.error) as Error)?.message ||
                  "Something went wrong"}
              </p>
            )}
            {emis.length > 0 ? (
              <div className={CARD_GRID}>
                {emis.map((e) => (
                  <EmiCard
                    key={e.id}
                    emi={e}
                    onDelete={(id) => deleteEmi.mutate(id)}
                    onMarkPaid={(id, current) =>
                      markEmiPaid.mutate({ id, paidMonths: current + 1 })
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-caption text-text-muted">No EMIs</p>
            )}
          </section>

          {/* Sinking funds section */}
          <section className="flex min-w-0 flex-col gap-3">
            <SectionHeader
              title="Sinking Funds"
              action={
                <Button variant="link" size="sm" onClick={() => setShowAddSinkingFund(true)}>
                  + Add fund
                </Button>
              }
            />
            {deleteSinkingFund.isError && (
              <p className="text-caption text-accent-amber">
                {(deleteSinkingFund.error as Error)?.message || "Something went wrong"}
              </p>
            )}
            {sinkingFunds.length > 0 ? (
              <Card size="sm" className="gap-0 divide-y divide-border-subtle px-4 py-0">
                {sinkingFunds.map((f) => (
                  <SinkingFundRow
                    key={f.id}
                    fund={f}
                    onDelete={(id) => deleteSinkingFund.mutate(id)}
                  />
                ))}
              </Card>
            ) : (
              <p className="py-4 text-center text-caption text-text-muted">No sinking funds</p>
            )}
          </section>
        </div>
        {sheets}
      </PageWrapper>
    </>
  )
}

const CARD_GRID = "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"

function SectionHeader({
  title,
  meta,
  action,
}: {
  title: string
  meta?: React.ReactNode
  action: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-2">
      <h2 className="min-w-0 truncate text-label uppercase tracking-wider text-text-muted">
        {title}{" "}
        {meta && <span className="normal-case tracking-normal text-text-secondary">{meta}</span>}
      </h2>
      {action}
    </div>
  )
}
